import React from 'react';
import { Link } from 'react-router-dom';
import {
  Plane,
  Hotel,
  Anchor,
  MapPin,
  CalendarClock,
  Car,
  Users,
  Luggage,
  CreditCard,
  Shield,
  Radar,
  MessageCircle,
  Phone,
  Calculator,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { contactInfo } from '../mock/fareData';

/*
 * 2026-09-06 — Stage 3 SEO landing page: /nassau-airport-transfers
 * Dedicated SEO page targeting Nassau airport transfers, LPIA airport
 * transportation, pre-book airport taxi Nassau Bahamas. Reuses the /services
 * page's visual system (cyan/blue gradient, rounded cards, cta panel).
 * Content is informational only:
 *   - No fare changes, no booking logic changes
 *   - No new backend routes
 *   - No modifications to notifications, payment processing, or FAQ data
 * All CTAs point to existing routes: /book, /fare-calculator, /services,
 * WhatsApp, tel:.
 */

const highlights = [
  {
    icon: CalendarClock,
    title: 'Pre-Booked Airport Pickup',
    desc: 'Reserve Taxi-98 before you arrive in The Bahamas. Share your travel date, arrival time and destination and your pickup will be scheduled in advance.'
  },
  {
    icon: Radar,
    title: 'Flight Monitoring',
    desc: 'When you provide your flight information with your reservation, Taxi-98 can monitor the arriving flight so we are aware of schedule changes that may affect your pickup.'
  },
  {
    icon: Car,
    title: 'Private Transportation',
    desc: 'Taxi-98 provides scheduled private airport transportation — not shared transport. Your reservation is dedicated to your travel party from LPIA to your destination.'
  },
  {
    icon: MapPin,
    title: 'Nassau, New Providence & Paradise Island',
    desc: 'We cover LPIA airport transfers across Nassau, New Providence and Paradise Island, including hotels, resorts, private residences and the Nassau Cruise Port.'
  },
  {
    icon: Luggage,
    title: 'Luggage Assistance',
    desc: 'Normal luggage assistance is provided at pickup. Bring your suitcases, carry-ons and travel bags — we will help you get everything loaded safely.'
  },
  {
    icon: Shield,
    title: 'Professional Service',
    desc: 'Clear communication, advance reservations and professional Bahamian drivers. Taxi-98 focuses on scheduled airport transportation you can plan around.'
  }
];

const hotels = [
  'Atlantis Paradise Island',
  'Baha Mar',
  'Grand Hyatt Baha Mar',
  'SLS Baha Mar',
  'Rosewood Baha Mar',
  'RIU Palace Paradise Island',
  'Warwick Paradise Island',
  'Comfort Suites Paradise Island',
  'Margaritaville Beach Resort Nassau',
  'Cable Beach hotels',
  'Downtown Nassau hotels',
  'Vacation rentals & Airbnbs'
];

const audience = [
  'Solo travelers',
  'Couples',
  'Families',
  'Small groups',
  'Business travelers',
  'Returning visitors'
];

const NassauAirportTransfers = () => {
  const siteUrl = 'https://taxi-98.com';
  const canonicalPath = '/nassau-airport-transfers';

  // Reuse existing SEOHead. Additional page-level JSON-LD (Service +
  // BreadcrumbList) is emitted inline so we don't create a second SEO utility.
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Nassau Airport Transfers — Private Transportation from LPIA',
    serviceType: 'Airport transfer',
    provider: {
      '@type': 'TaxiService',
      name: 'Taxi-98',
      url: siteUrl,
      areaServed: [
        { '@type': 'Place', name: 'Nassau' },
        { '@type': 'Place', name: 'New Providence' },
        { '@type': 'Place', name: 'Paradise Island' }
      ]
    },
    areaServed: [
      { '@type': 'Place', name: 'Lynden Pindling International Airport (LPIA)' },
      { '@type': 'Place', name: 'Nassau, Bahamas' },
      { '@type': 'Place', name: 'Paradise Island, Bahamas' }
    ],
    url: `${siteUrl}${canonicalPath}`,
    description:
      'Pre-booked private airport transportation from Lynden Pindling International Airport (LPIA) to Nassau, New Providence and Paradise Island. Flight monitoring available when flight information is provided at reservation.'
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl + '/' },
      { '@type': 'ListItem', position: 2, name: 'Services', item: siteUrl + '/services' },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Nassau Airport Transfers',
        item: siteUrl + canonicalPath
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="Nassau Airport Transfers | LPIA Private Transportation | Taxi-98 Bahamas"
        description="Pre-book private Nassau airport transportation with Taxi-98. Scheduled transfers from Lynden Pindling International Airport (LPIA) to Nassau and Paradise Island, with flight monitoring when flight details are provided."
        canonical={canonicalPath}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <main
        data-testid="airport-transfers-page"
        className="relative min-h-screen bg-gradient-to-br from-cyan-50 via-white to-blue-50 pt-28 pb-20 sm:pt-32 sm:pb-28"
      >
        {/* Subtle accents */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-400/5 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="max-w-3xl mx-auto text-center mb-14 sm:mb-16">
            <span
              className="inline-block bg-cyan-500/10 border border-cyan-400/30 text-cyan-700 text-xs font-bold tracking-[0.22em] uppercase px-4 py-1.5 rounded-full mb-5"
              data-testid="airport-transfers-eyebrow"
            >
              LPIA · Nassau · Paradise Island
            </span>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-5"
              data-testid="airport-transfers-headline"
            >
              Nassau Airport Transfers — Private Transportation from LPIA
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
              Taxi-98 provides pre-booked, scheduled private transportation from
              Lynden Pindling International Airport (LPIA) to Nassau, New
              Providence and Paradise Island. Reserve in advance and travel on
              your own schedule.
            </p>

            <div
              className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
              data-testid="airport-transfers-hero-ctas"
            >
              <Link
                to="/book"
                data-testid="airport-transfers-cta-book"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-600 to-blue-700 hover:from-cyan-700 hover:to-blue-800 text-white px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300 shadow-md"
              >
                <span>Book Your Airport Transfer</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/fare-calculator"
                data-testid="airport-transfers-cta-fare"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white border border-cyan-300 text-cyan-700 hover:bg-cyan-50 px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300"
              >
                <Calculator className="w-4 h-4" />
                <span>Check Your Fare</span>
              </Link>
            </div>

            <p
              className="mt-5 text-sm text-gray-500 flex items-center justify-center gap-2 flex-wrap"
              data-testid="airport-transfers-payments-compact"
            >
              <CheckCircle className="w-4 h-4 text-cyan-600" />
              <span>
                Pre-Booking Available • Major Credit Cards • Apple Pay / Tap-to-Pay • PayPal • Cash Accepted
              </span>
            </p>
          </div>

          {/* Highlights grid */}
          <section aria-labelledby="airport-highlights-heading" className="mb-16">
            <h2
              id="airport-highlights-heading"
              className="sr-only"
            >
              Nassau airport transfer highlights
            </h2>
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6"
              data-testid="airport-transfers-highlights"
            >
              {highlights.map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  data-testid={`airport-highlight-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                  className="group bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-lg hover:border-cyan-300 transition-all duration-300"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-500/10 text-cyan-600 mb-4 group-hover:bg-cyan-500/15 transition-colors">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Hotels & Resorts */}
          <section
            aria-labelledby="airport-hotels-heading"
            className="mb-16 bg-white border border-gray-200 rounded-3xl p-8 sm:p-10 shadow-sm"
            data-testid="airport-hotels-section"
          >
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 text-cyan-700 font-semibold uppercase text-xs tracking-widest mb-3">
                <Hotel className="w-4 h-4" />
                Hotels & Resorts
              </div>
              <h2
                id="airport-hotels-heading"
                className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4"
              >
                Airport transportation to Nassau hotels, resorts and rentals
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Taxi-98 provides pre-booked airport transportation to hotels and
                resorts across Nassau, Cable Beach and Paradise Island, as well
                as private vacation rentals across New Providence.
              </p>
            </div>
            <ul
              className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3"
              data-testid="airport-hotels-list"
            >
              {hotels.map((hotel) => (
                <li
                  key={hotel}
                  className="flex items-start gap-2 text-gray-700"
                >
                  <CheckCircle className="w-4 h-4 mt-1 text-cyan-600 shrink-0" />
                  <span>{hotel}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Cruise + Who We Serve */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6 mb-16">
            <section
              aria-labelledby="airport-cruise-heading"
              className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm"
              data-testid="airport-cruise-section"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-500/10 text-cyan-600 mb-4">
                <Anchor className="w-6 h-6" />
              </div>
              <h2
                id="airport-cruise-heading"
                className="text-xl sm:text-2xl font-bold text-gray-900 mb-2"
              >
                Cruise Connections
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Taxi-98 provides pre-booked transportation between LPIA and the
                Nassau Cruise Port. Cruise passengers can also arrange pickup
                and drop-off from the port for Nassau and Paradise Island rides.
              </p>
            </section>

            <section
              aria-labelledby="airport-audience-heading"
              className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm"
              data-testid="airport-audience-section"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-500/10 text-cyan-600 mb-4">
                <Users className="w-6 h-6" />
              </div>
              <h2
                id="airport-audience-heading"
                className="text-xl sm:text-2xl font-bold text-gray-900 mb-3"
              >
                Who We Serve
              </h2>
              <ul
                className="grid grid-cols-2 gap-x-4 gap-y-2 text-gray-700 text-sm"
                data-testid="airport-audience-list"
              >
                {audience.map((a) => (
                  <li key={a} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 text-cyan-600 shrink-0" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          {/* Payment Options */}
          <section
            aria-labelledby="airport-payments-heading"
            className="mb-16 bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm"
            data-testid="airport-payments-section"
          >
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-500/10 text-cyan-600 mb-4">
              <CreditCard className="w-6 h-6" />
            </div>
            <h2
              id="airport-payments-heading"
              className="text-xl sm:text-2xl font-bold text-gray-900 mb-3"
            >
              Payment Options
            </h2>
            <p className="text-gray-700 mb-2 font-semibold">
              Pre-Booking Available • Major Credit Cards • Apple Pay / Tap-to-Pay • PayPal • Cash Accepted
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Visa, Mastercard, American Express, Discover, Apple Pay /
              Tap-to-Pay, PayPal and Cash. Payment can be completed at pickup or
              confirmed after booking.
            </p>
          </section>

          {/* Trust positioning */}
          <section
            aria-labelledby="airport-trust-heading"
            className="mb-16"
            data-testid="airport-trust-section"
          >
            <div className="max-w-3xl mb-6">
              <h2
                id="airport-trust-heading"
                className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3"
              >
                What travelers rely on Taxi-98 for
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Every airport reservation is handled the same way: scheduled,
                private and pre-planned. Here is what we focus on.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                'Scheduled airport transportation',
                'Clear, direct communication',
                'Flight monitoring when flight information is provided',
                'Private transportation for your travel party',
                'Advance reservations you can plan around',
                'Professional Bahamian drivers',
                'Airport, hotel and resort transportation',
                'Nassau, New Providence and Paradise Island coverage'
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-start gap-3 bg-white border border-gray-200 rounded-xl p-4"
                >
                  <CheckCircle className="w-5 h-5 mt-0.5 text-cyan-600 shrink-0" />
                  <p className="text-gray-700 text-sm">{item}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Internal links */}
          <section
            aria-labelledby="airport-related-heading"
            className="mb-16"
            data-testid="airport-related-links"
          >
            <h2
              id="airport-related-heading"
              className="text-xl sm:text-2xl font-bold text-gray-900 mb-4"
            >
              Plan the rest of your Nassau ride
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link
                to="/book"
                data-testid="airport-related-book"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <Plane className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">Book a ride</p>
                <p className="text-sm text-gray-600">
                  Reserve your LPIA airport transfer.
                </p>
              </Link>
              <Link
                to="/fare-calculator"
                data-testid="airport-related-fare"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <Calculator className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">Fare Calculator</p>
                <p className="text-sm text-gray-600">
                  Estimate your Nassau airport fare.
                </p>
              </Link>
              <Link
                to="/services"
                data-testid="airport-related-services"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <Car className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">All Services</p>
                <p className="text-sm text-gray-600">
                  See every Taxi-98 service in Nassau.
                </p>
              </Link>
              <Link
                to="/#faq"
                data-testid="airport-related-faq"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <MessageCircle className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">Airport FAQs</p>
                <p className="text-sm text-gray-600">
                  Common questions from arriving travelers.
                </p>
              </Link>
              <Link
                to="/lpia-to-atlantis"
                data-testid="airport-related-atlantis"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <MapPin className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">LPIA to Atlantis Transportation</p>
                <p className="text-sm text-gray-600">
                  Airport transfers to Atlantis Paradise Island.
                </p>
              </Link>
              <Link
                to="/lpia-to-baha-mar"
                data-testid="airport-related-bahamar"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <Hotel className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">LPIA to Baha Mar Transportation</p>
                <p className="text-sm text-gray-600">
                  Airport transfers to the Baha Mar resort area.
                </p>
              </Link>
              <Link
                to="/cruise-port-transportation"
                data-testid="airport-related-cruise"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <Anchor className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">Nassau Cruise Port Transportation</p>
                <p className="text-sm text-gray-600">
                  Cruise-port pickup, drop-off and resort rides.
                </p>
              </Link>
            </div>
          </section>

          {/* CTA panel (same visual as /services) */}
          <div
            className="bg-gradient-to-br from-cyan-600 to-blue-700 rounded-3xl p-8 sm:p-10 lg:p-12 text-white shadow-xl"
            data-testid="airport-transfers-cta-panel"
          >
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3">
                Ready to reserve your LPIA airport transfer?
              </h2>
              <p className="text-base sm:text-lg text-cyan-50 mb-8">
                Pre-book online, estimate your fare in seconds, or reach a real
                person on WhatsApp or by phone. We are happy to help with
                anything special you need.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                <Link
                  to="/book"
                  data-testid="airport-cta-book-now"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-cyan-700 hover:bg-cyan-50 px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300 shadow-md"
                >
                  <span>Book Your Airport Transfer</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>

                <Link
                  to="/fare-calculator"
                  data-testid="airport-cta-fare-calculator"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-cyan-700/40 border border-white/30 hover:bg-cyan-700/60 text-white px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300 backdrop-blur-sm"
                >
                  <Calculator className="w-4 h-4" />
                  <span>Check Your Fare</span>
                </Link>

                <a
                  href={`https://wa.me/${contactInfo.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(
                    'Hello Taxi-98, I would like to pre-book a Nassau airport transfer.'
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="airport-cta-whatsapp"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300 shadow-md"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>WhatsApp Us</span>
                </a>
              </div>

              <div className="mt-7 pt-7 border-t border-white/20 text-sm sm:text-base text-cyan-50">
                <p className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
                  <span className="inline-flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <a
                      href={`tel:${contactInfo.phone.replace(/[^+\d]/g, '')}`}
                      data-testid="airport-cta-phone"
                      className="font-semibold hover:underline"
                    >
                      {contactInfo.phone}
                    </a>
                  </span>
                  <span className="hidden sm:inline opacity-50">•</span>
                  <span className="opacity-90">{contactInfo.hours}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default NassauAirportTransfers;
